import { PayloadAction, createAsyncThunk, createEntityAdapter, createSlice } from "@reduxjs/toolkit";
import { RootState } from "../../../app/store";
import * as workoutPlanService from '../../../service/workout-plan.service'
import { WorkoutPlan, WorkoutPlanRequest } from "../../../service/model/workout.model";
import { showNotification } from "../../alerts/alert.slice";
import { NotificationType } from "../../../shared/enums";
import { getAxiosErrorMessage } from "../../../service/axios/axios.call.service";

export interface WorkoutPlanState {
    isLoading: boolean;
    activePlan?: WorkoutPlan;
}

const workoutPlansAdapter = createEntityAdapter({
    selectId: (workoutPlan: WorkoutPlan) => workoutPlan.id,
    sortComparer: (a, b) => a.startDate?.localeCompare(b.startDate),
  })

export const initialState = workoutPlansAdapter.getInitialState({
    isLoading: false,
} as WorkoutPlanState);

export const fetchWorkoutPlans = createAsyncThunk<WorkoutPlan[], number>(
    'workoutPlans/all',
    async (memberId, {rejectWithValue}) => {
        return workoutPlanService.getWorkoutPlans(memberId).catch((errors) => {
            return rejectWithValue(errors);
        })
    }
);

export const fetchActivePlan = createAsyncThunk<WorkoutPlan, number>(
    'workoutPlans/active',
    async (memberId, {rejectWithValue}) => {
        return workoutPlanService.getActivePlan(memberId).catch((errors) => {
            return rejectWithValue(errors);
        })
    }
);

export const createWorkoutPlan = createAsyncThunk<
    WorkoutPlan,
    WorkoutPlanRequest
>('workoutPlans/create', async (request, { rejectWithValue, dispatch }) => {
    return workoutPlanService
        .createWorkoutPlan(request)
        .then((response) => {
            dispatch(
                showNotification({
                    message: 'Workout plan created successfully',
                    type: NotificationType.Success,
                })
            );
            dispatch(fetchActivePlan(request.memberId));
            return response;
        })
        .catch((errors) => {
            dispatch(
                showNotification({
                    message: getAxiosErrorMessage(errors.response),
                    type: NotificationType.Error,
                })
            );
            return rejectWithValue(errors);
        });
});

export const updateWorkoutPlan = createAsyncThunk<
    WorkoutPlan,
    WorkoutPlanRequest
>('workoutPlans/update', async (request, { rejectWithValue, dispatch }) => {
    return workoutPlanService
        .updateWorkoutPlan(request)
        .then((response) => {
            dispatch(
                showNotification({
                    message: 'Workout plan updated successfully',
                    type: NotificationType.Success,
                })
            );
            dispatch(fetchActivePlan(request.memberId));
            return response;
        })
        .catch((errors) => {
            dispatch(
                showNotification({
                    message: getAxiosErrorMessage(errors.response),
                    type: NotificationType.Error,
                })
            );
            return rejectWithValue(errors);
        });
});

export const deleteWorkoutPlan = createAsyncThunk<
    number,
    WorkoutPlan
>('workoutPlans/delete', async (plan, { rejectWithValue, dispatch }) => {
    return workoutPlanService
        .deleteWorkoutPlan(plan.id)
        .then((response) => {
            dispatch(
                showNotification({
                    message: 'Workout plan deleted successfully',
                    type: NotificationType.Success,
                })
            );
            dispatch(fetchWorkoutPlans(plan.memberId));
            return response;
        })
        .catch((errors) => {
            dispatch(
                showNotification({
                    message: getAxiosErrorMessage(errors.response),
                    type: NotificationType.Error,
                })
            );
            return rejectWithValue(errors);
        });
});

const workoutPlanSlice = createSlice({
    name: 'workout-plans',
    initialState,
    reducers: {
        resetWorkoutPlan: (state) => {
            state.activePlan = undefined;
        },
        updateActivePlan: (state, action: PayloadAction<WorkoutPlan>) => {
            state.activePlan = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
        .addCase(fetchWorkoutPlans.pending, (state) => {
            state.isLoading = true;
        })
        .addCase(fetchWorkoutPlans.fulfilled, (state, action: PayloadAction<WorkoutPlan[]>) => {
            workoutPlansAdapter.setAll(state, action.payload);
            state.isLoading = false;
        })
        .addCase(fetchWorkoutPlans.rejected, (state) => {
            state.isLoading = false;
        })
        .addCase(createWorkoutPlan.pending, (state) => {
            state.isLoading = true;
        })
        .addCase(fetchActivePlan.pending, (state) => {
            state.isLoading = true;
        })
        .addCase(fetchActivePlan.fulfilled, (state, action: PayloadAction<WorkoutPlan>) => {
            state.activePlan = action.payload;
            state.isLoading = false;
        })
        .addCase(fetchActivePlan.rejected, (state) => {
            state.isLoading = false;
        })
        .addCase(createWorkoutPlan.fulfilled, (state) => {
            state.isLoading = false;
        })
        .addCase(createWorkoutPlan.rejected, (state) => {
            state.isLoading = false;
        })
        .addCase(updateWorkoutPlan.pending, (state) => {
            state.isLoading = true;
        })
        .addCase(updateWorkoutPlan.fulfilled, (state) => {
            state.isLoading = false;
        })
        .addCase(updateWorkoutPlan.rejected, (state) => {
            state.isLoading = false;
        })
        .addCase(deleteWorkoutPlan.pending, (state) => {
            state.isLoading = true;
        })
        .addCase(deleteWorkoutPlan.fulfilled, (state) => {
            state.isLoading = false;
        })
        .addCase(deleteWorkoutPlan.rejected, (state) => {
            state.isLoading = false;
        });
    }
});

export const {
    selectAll: selectWorkoutPlans,
    selectById: selectWorkoutPlanById
} = workoutPlansAdapter.getSelectors<RootState>((state) => state.workoutPlans);

export const {
    resetWorkoutPlan,
    updateActivePlan
} = workoutPlanSlice.actions;

export const selectIsLoading = (state: RootState) => state.workoutPlans.isLoading;
export const selectActivePlan = (state: RootState) => state.workoutPlans.activePlan;

export default workoutPlanSlice.reducer;